import React, { useState, useEffect, useRef } from 'react';
import { Package, AlertCircle, CheckCircle, RefreshCw, List, Check, ArrowRight, ChevronDown, ChevronRight, Truck, Ship, X, Plus } from 'lucide-react';
import { pOrderLineService } from '../../../../services/pOrderLineService';
import { POrderLineDto, POrdersDto } from '../../../../types/porders';
import { StcTransLineStatus } from '../../../../types/stcTrans';
import { StcTransDto, stcTransService, CreateStcTransRequest } from '../../../../services/stcTransService';
import { pOrdersService } from '../../../../services/pOrdersService';
import { bilmottakService } from '../../../../services/bilmottakService';
import { POrderLineBrowser } from './search/POrderLineBrowser';
import { PutAwayModal } from './PutAwayModal';
import { StcTransEditModal } from './StcTransEditModal';
import { DirectReceiptModal } from './DirectReceiptModal';
import { OverReceiveConfirmModal } from './OverReceiveConfirmModal';
import { userProfileService } from '../../../../services/userProfileService';
import { UserProfileDto } from '../../../../types/userProfile';
import { itemsService } from '../../../../services/itemsService';
import { BackButton } from '../../../common/BackButton';
import { MediaCaptureButton } from '../../../common/media/MediaCaptureButton';
import { ReceivingActiveLine } from './ReceivingActiveLine';
import { AdvancedItemConfigModal } from './AdvancedItemConfigModal';
import { SerialNumberReceiptModal } from './modals/SerialNumberReceiptModal';
import { printingService } from '../../../../services/printingService';
import { useSimpleBarcodeScanner } from '../../../../hooks/useSimpleBarcodeScanner';
import { MobileBarcodeCaptureInput } from '../../../common/scanning';
import { useBarcodeScannerEnabled } from '../../../../hooks/useBarcodeScannerEnabled';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { buildStcTransUpdateRequest, updateTransactionWithFallback, processBarcodeInput } from './helpers';
import { useWarehouse } from '../../../../contexts/WarehouseContext';

/** Receiving mode determines how the component filters and validates items */
type ReceivingMode = 'order' | 'shipment' | 'bilmottak';

interface POrderReceivingDetailProps {
    /** Order number for Modus 1 (PO-based receiving) */
    orderNo?: number;
    /** Shipment number for Modus 3 (Inbound Shipment receiving) */
    inboundShipmentNo?: string;
    /** BatchID for Modus 4 (Bilmottak/Truck receiving) - shared BatchID for multi-operator receiving */
    bilmottakBatchId?: string;
    /** Receiving mode: 'order' = filter by orderNo, 'shipment' = filter by shipmentNo, 'bilmottak' = multi-operator truck receiving */
    receivingMode?: ReceivingMode;
    onBack: () => void;
    onNavigateToPutAway?: (stcOrderNo: number) => void;
}

interface ReceivingLine {
    orderLine: POrderLineDto;
    receivedTransactions: StcTransDto[];
    totalReceived: number;
    remainingQuantity: number;
}

interface BatchEditState {
    [transactionId: string]: {
        itemBatchNo: string;
        itemBatchExpDate: string;
        quantity: number;
        isEditing: boolean;
        isSaving: boolean;
        isEditingQuantity: boolean;
    };
}

export const POrderReceivingDetail: React.FC<POrderReceivingDetailProps> = ({
    orderNo,
    inboundShipmentNo,
    bilmottakBatchId,
    receivingMode = 'order',
    onBack,
    onNavigateToPutAway
}) => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouse();
    const [order, setOrder] = useState<POrdersDto | null>(null);
    // For shipment mode, we may have multiple orders - track the first one for display
    const [shipmentInfo, setShipmentInfo] = useState<{ shipmentNo: string; supplierNames: string } | null>(null);
    // For bilmottak mode, track session info
    const [bilmottakInfo, setBilmottakInfo] = useState<{ batchId: string; name: string; warehouse: string } | null>(null);
    const [receivingLines, setReceivingLines] = useState<ReceivingLine[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [newReceipt, setNewReceipt] = useState<{ lineNo: number; quantity: number; orderNo?: number; } | null>(null);
    const [batchId, setBatchId] = useState<string>('');
    const [showLineBrowser, setShowLineBrowser] = useState(false);
    const [selectedSearchLine, setSelectedSearchLine] = useState<POrderLineDto | null>(null);
    const [availableOrderLines, setAvailableOrderLines] = useState<POrderLineDto[]>([]);
    const [hasTransactions, setHasTransactions] = useState(false);
    const [canFinishReceiving, setCanFinishReceiving] = useState(false);
    const [isFinishingReceiving, setIsFinishingReceiving] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [inputJustFocused, setInputJustFocused] = useState(false);
    const [showPutAwayModal, setShowPutAwayModal] = useState(false);
    const [selectedStcTrans, setSelectedStcTrans] = useState<StcTransDto | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDirectReceiptModal, setShowDirectReceiptModal] = useState(false);
    const [batchEditState, setBatchEditState] = useState<BatchEditState>({});
    const [showLockedLines, setShowLockedLines] = useState(false);
    const [editingStcTrans, setEditingStcTrans] = useState<StcTransDto | null>(null);
    const [selectedLineItemType, setSelectedLineItemType] = useState<number | undefined>(undefined);
    const [showAdvancedConfigModal, setShowAdvancedConfigModal] = useState(false);
    const [advancedConfigItemNo, setAdvancedConfigItemNo] = useState<string | null>(null);
    const [allowAdvItemConfig, setAllowAdvItemConfig] = useState(false);
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);
    const [bulkProcessingMessage, setBulkProcessingMessage] = useState('');
    const [userProfile, setUserProfile] = useState<UserProfileDto | null>(null);
    const [warningMessage, setWarningMessage] = useState<string>('');
    const [validationError, setValidationError] = useState<string>('');
    const [validationWarning, setValidationWarning] = useState<string>('');
    const [showOverReceiveConfirmModal, setShowOverReceiveConfirmModal] = useState(false);
    const [pendingOverReceiveAction, setPendingOverReceiveAction] = useState<(() => Promise<void>) | null>(null);
    const [overReceiveModalMessage, setOverReceiveModalMessage] = useState('');
    const [showAddPOModal, setShowAddPOModal] = useState(false);
    const [addPOAvailableOrders, setAddPOAvailableOrders] = useState<POrdersDto[]>([]);
    const [addPOSelectedNos, setAddPOSelectedNos] = useState<Set<number>>(new Set());
    const [addPOLoading, setAddPOLoading] = useState(false);
    const [addPOSearchTerm, setAddPOSearchTerm] = useState('');
    // Serial number modal state (ItemType=6)
    const [showSerialNumberModal, setShowSerialNumberModal] = useState(false);
    const [serialNumberPendingData, setSerialNumberPendingData] = useState<{
        lineNo: number;
        quantity: number;
        batchNo?: string;
        expDate?: string;
        receivingLocation?: string;
        action: 'save' | 'directReceipt';
        itemNo: string;
        itemDescription: string;
    } | null>(null);

    const quantityInputRef = useRef<HTMLInputElement>(null);
    const scannerRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        loadOrderData();
    }, [orderNo, inboundShipmentNo, bilmottakBatchId, receivingMode]);

    // Load user profile to check advanced config permission
    useEffect(() => {
        const loadUserProfile = async () => {
            try {
                const profile = await userProfileService.getCurrentUserProfile();
                console.log('🔍 User Profile Loaded:', profile);
                console.log('🔍 extraQtyOnGRNotAllowed:', profile.extraQtyOnGRNotAllowed);
                console.log('🔍 extraQtyOnGRConfirm:', profile.extraQtyOnGRConfirm);
                setUserProfile(profile);  // Store full profile
                setAllowAdvItemConfig(profile.allowAdvItemConfigYesNo || false);
            } catch (error) {
                console.error('Error loading user profile:', error);
                setAllowAdvItemConfig(false);
            }
        };
        loadUserProfile();
    }, []);

    // Fetch itemType when selectedSearchLine changes
    useEffect(() => {
        const fetchItemType = async () => {
            if (selectedSearchLine && order) {
                try {
                    const item = await itemsService.getItemByCompanyAndItemNo(
                        order.companyNo,
                        selectedSearchLine.itemNo
                    );
                    setSelectedLineItemType(item.itemType);
                } catch (error) {
                    console.warn('Could not fetch itemType for selected line:', error);
                    setSelectedLineItemType(undefined);
                }
            } else {
                setSelectedLineItemType(undefined);
            }
        };
        fetchItemType();
    }, [selectedSearchLine, order]);

    // Auto-focus disabled - global scanning is active
    // useEffect(() => {
    //     if (newReceipt && quantityInputRef.current) {
    //         quantityInputRef.current.focus();
    //         const input = quantityInputRef.current;
    //         setTimeout(() => {
    //             const length = input.value.length;
    //             input.setSelectionRange(length, length);
    //         }, 0);
    //     }
    // }, [newReceipt]);

    const checkCanFinishReceiving = async (batchId: string) => {
        try {
            console.log('Checking if receiving can be finished for batch:', batchId);
            const result = await stcTransService.canFinishReceiving(batchId);
            console.log('Can finish receiving result:', result);
            setCanFinishReceiving(result.canFinish);
            if (!result.canFinish) {
                console.log('Cannot finish receiving:', result.message);
            }
        } catch (error) {
            console.error('Error checking if receiving can be finished:', error);
            setCanFinishReceiving(false);
        }
    };

    const handleFinishReceiving = async () => {
        if (!batchId) return;

        try {
            setIsFinishingReceiving(true);
            await stcTransService.finishReceiving(batchId);

            setShowSuccessMessage(true);
            setError(null);

            setTimeout(() => {
                onBack();
            }, 2000);

        } catch (error) {
            console.error('Error finishing receiving:', error);
            setError(`Kunne ikke avslutte mottak: ${error instanceof Error ? error.message : 'Ukjent feil'}`);
        } finally {
            setIsFinishingReceiving(false);
        }
    };

    const handleFinishReceivingAndPutAway = async () => {
        if (!batchId || !order) return;

        try {
            setIsFinishingReceiving(true);
            setError(null);

            // Call the endpoint to finish receiving and generate put-away tasks
            // Backend performs bulk ItemBinSize validation before processing
            const result = await stcTransService.finishReceivingAndPutAway(batchId, {
                orderNo: order.orderNo
            });

            // Backend bulk pre-validation: items missing ItemBinSize configuration
            if (result.itemsMissingBinConfig && result.itemsMissingBinConfig.length > 0) {
                setError(`Følgende varer mangler bin-konfigurasjon og kan ikke plasseres automatisk: ${result.itemsMissingBinConfig.join(', ')}. Gå til Administrasjon > Put-Away Diagnostikk for detaljer, eller bruk "Avslutt mottak" for å motta uten automatisk plassering.`);
                setIsFinishingReceiving(false);
                return;
            }

            if (result.success) {
                setShowSuccessMessage(true);

                // Show messages from backend
                if (result.messages && result.messages.length > 0) {
                    console.log('Process messages:', result.messages);
                }

                // Navigate to put-away module after a short delay
                setTimeout(() => {
                    if (onNavigateToPutAway && result.stcOrderNo) {
                        onNavigateToPutAway(result.stcOrderNo);
                    } else if (onNavigateToPutAway) {
                        // If no stcOrderNo but function exists, call without parameter (will show list)
                        console.warn('No StcOrderNo received from backend, navigating to put-away list');
                        onNavigateToPutAway(0);
                    } else {
                        // Fallback to going back if navigation function not provided
                        onBack();
                    }
                }, 1500);
            } else {
                // Handle partial success or failure with improved error messages
                if (result.receivingFinished && !result.putAwayTasksGenerated) {
                    // Parse messages for item-specific errors
                    const itemErrors = result.messages
                        ?.filter(m => m.includes('No bins found for item') || m.includes('Failed to create put-away reservations'))
                        .map(m => {
                            const match = m.match(/item[:\s]+(\S+)/i);
                            return match ? match[1] : null;
                        })
                        .filter(Boolean) || [];

                    if (itemErrors.length > 0) {
                        setError(`Mottak fullført, men plassering feilet for: ${itemErrors.join(', ')}. Disse varene mangler bin-konfigurasjon (ItemBinSize). Gå til Administrasjon > Put-Away Diagnostikk for detaljer.`);
                    } else {
                        setError('Mottak avsluttet, men plasseringsoppgaver ble ikke opprettet. Se Administrasjon > Put-Away Diagnostikk for detaljer.');
                    }
                } else {
                    setError(result.errorMessage || 'Kunne ikke fullføre operasjonen.');
                }
            }

        } catch (error) {
            console.error('Error finishing receiving and generating put-away:', error);
            setError(`Kunne ikke fullføre operasjonen: ${error instanceof Error ? error.message : 'Ukjent feil'}`);
        } finally {
            setIsFinishingReceiving(false);
        }
    };

    const handleOpenAdvancedConfig = (itemNo: string) => {
        setAdvancedConfigItemNo(itemNo);
        setShowAdvancedConfigModal(true);
    };

    const handleCloseAdvancedConfig = () => {
        setShowAdvancedConfigModal(false);
        setAdvancedConfigItemNo(null);
    };

    const handleAdvancedConfigSaved = () => {
        // Refresh order data after saving config
        loadOrderData();
    };

    const loadOrderData = async (bustCache: boolean = false) => {
        try {
            setLoading(true);
            setError(null);

            let orderLines: POrderLineDto[];
            let usedBatchId: string;

            if (receivingMode === 'shipment' && inboundShipmentNo) {
                // Modus 3: Inbound Shipment - load lines by shipment number
                console.log('Henter forsendelseslinjer for shipmentNo:', inboundShipmentNo, bustCache ? '(cache-busting enabled)' : '');

                // Get all PO lines for this shipment (may span multiple orders)
                const shipmentLines = await pOrderLineService.getShipmentLines(inboundShipmentNo);

                if (!shipmentLines || shipmentLines.length === 0) {
                    console.warn('Ingen linjer funnet for forsendelse', inboundShipmentNo);
                    setReceivingLines([]);
                    setShipmentInfo({ shipmentNo: inboundShipmentNo, supplierNames: '' });
                    setLoading(false);
                    return;
                }

                // Convert POrderLineWithHeaderDto to POrderLineDto format
                orderLines = shipmentLines.map(line => ({
                    id: line.id,
                    companyNo: line.companyNo,
                    orderNo: line.orderNo,
                    lineNo: line.lineNo,
                    lineNoERP: line.lineNoERP,
                    lineStatus: line.lineStatus,
                    itemNo: line.itemNo,
                    itemBarCode: line.itemBarCode,
                    description1: line.description1,
                    itemGroup1: line.itemGroup1,
                    itemGroup2: line.itemGroup2,
                    itemGroup3: line.itemGroup3,
                    quantity: line.quantity,
                    quantityDelivered: line.quantityDelivered,
                    itemUnitCode: line.itemUnitCode,
                    itemUnitTxt: line.itemUnitTxt,
                    price: line.price,
                    discount: line.discount,
                    deliveryDate: line.deliveryDate,
                    serialNoItemYesNo: line.serialNoItemYesNo,
                    stdLocationName: line.stdLocationName,
                    warehouse: line.warehouse,
                    orderNoERP: line.orderNoERP,
                    itemBatchNo: line.itemBatchNo,
                    batchItemYesNo: line.batchItemYesNo,
                    orderLineFreeField1: line.orderLineFreeField1,
                    orderLineFreeField2: line.orderLineFreeField2,
                    orderLineFreeField3: line.orderLineFreeField3,
                    inboundShipmentNo: line.inboundShipmentNo,
                    statusText: line.lineStatusText,
                    remainingQuantity: line.remainingQuantity
                } as POrderLineDto));

                // Get unique supplier names from the lines
                const uniqueSuppliers = Array.from(new Set(shipmentLines.map(l => l.supplierName).filter(Boolean)));
                setShipmentInfo({
                    shipmentNo: inboundShipmentNo,
                    supplierNames: uniqueSuppliers.join(', ')
                });

                // For shipment mode, create a batch ID based on shipment number
                // Use first order's batchId pattern but include shipment reference
                const firstOrderNo = shipmentLines[0].orderNo;
                usedBatchId = await stcTransService.getOrCreateBatchId(firstOrderNo);
                console.log('Using batchId for shipment receiving:', usedBatchId);

                // Set a synthetic order for display purposes (use first line's order info)
                const firstLine = shipmentLines[0];
                setOrder({
                    id: 0,
                    companyNo: firstLine.companyNo,
                    orderNo: firstLine.orderNo,
                    orderNoERP: inboundShipmentNo, // Display shipment no in header
                    orderStatus: firstLine.orderStatus,
                    supplierNo: '',
                    supplierName: uniqueSuppliers.join(', '),
                    statusText: 'Forsendelse',
                    warehouse: firstLine.warehouse // Important for loading receiving locations
                } as POrdersDto);

            } else if (receivingMode === 'bilmottak' && bilmottakBatchId) {
                // Modus 4: Bilmottak - multi-operator truck receiving with shared BatchID
                // Loads lines via InboundShipmentNo = batchId (Modus 3-style browsing)
                console.log('Bilmottak modus - loading linked PO lines for BatchID:', bilmottakBatchId);

                // Set the BatchID directly - it was already created when session was started
                usedBatchId = bilmottakBatchId;

                // Load linked PO lines (InboundShipmentNo = batchId), including completed lines
                const shipmentLines = await pOrderLineService.getShipmentLines(bilmottakBatchId, true);

                if (shipmentLines && shipmentLines.length > 0) {
                    // Convert POrderLineWithHeaderDto to POrderLineDto format (same as Modus 3)
                    orderLines = shipmentLines.map(line => ({
                        id: line.id,
                        companyNo: line.companyNo,
                        orderNo: line.orderNo,
                        lineNo: line.lineNo,
                        lineNoERP: line.lineNoERP,
                        lineStatus: line.lineStatus,
                        itemNo: line.itemNo,
                        itemBarCode: line.itemBarCode,
                        description1: line.description1,
                        itemGroup1: line.itemGroup1,
                        itemGroup2: line.itemGroup2,
                        itemGroup3: line.itemGroup3,
                        quantity: line.quantity,
                        quantityDelivered: line.quantityDelivered,
                        itemUnitCode: line.itemUnitCode,
                        itemUnitTxt: line.itemUnitTxt,
                        price: line.price,
                        discount: line.discount,
                        deliveryDate: line.deliveryDate,
                        serialNoItemYesNo: line.serialNoItemYesNo,
                        stdLocationName: line.stdLocationName,
                        warehouse: line.warehouse,
                        orderNoERP: line.orderNoERP,
                        itemBatchNo: line.itemBatchNo,
                        batchItemYesNo: line.batchItemYesNo,
                        orderLineFreeField1: line.orderLineFreeField1,
                        orderLineFreeField2: line.orderLineFreeField2,
                        orderLineFreeField3: line.orderLineFreeField3,
                        inboundShipmentNo: line.inboundShipmentNo,
                        statusText: line.lineStatusText,
                        remainingQuantity: line.remainingQuantity
                    } as POrderLineDto));

                    const uniqueSuppliers = Array.from(new Set(shipmentLines.map(l => l.supplierName).filter(Boolean)));
                    const firstLine = shipmentLines[0];

                    setBilmottakInfo({
                        batchId: bilmottakBatchId,
                        name: 'Bilmottak',
                        warehouse: firstLine.warehouse || ''
                    });

                    // Set synthetic order for UI compatibility
                    setOrder({
                        id: 0,
                        companyNo: firstLine.companyNo,
                        orderNo: firstLine.orderNo,
                        orderNoERP: bilmottakBatchId,
                        orderStatus: 20,
                        supplierNo: '',
                        supplierName: uniqueSuppliers.join(', ') || 'Bilmottak',
                        statusText: 'Under arbeid',
                        warehouse: firstLine.warehouse || ''
                    } as POrdersDto);
                } else {
                    // No linked POs yet - show empty state
                    console.log('Bilmottak has no linked PO lines yet');
                    orderLines = [];

                    setBilmottakInfo({
                        batchId: bilmottakBatchId,
                        name: 'Bilmottak',
                        warehouse: ''
                    });

                    setOrder({
                        id: 0,
                        companyNo: '',
                        orderNo: 0,
                        orderNoERP: bilmottakBatchId,
                        orderStatus: 20,
                        supplierNo: '',
                        supplierName: 'Bilmottak',
                        statusText: 'Under arbeid',
                        warehouse: ''
                    } as POrdersDto);
                }

            } else if (orderNo) {
                // Modus 1: PO-based receiving - existing logic
                console.log('Henting ordre-informasjon for orderNo:', orderNo, bustCache ? '(cache-busting enabled)' : '');
                const orderData = await pOrdersService.getOrderByOrderNo(orderNo);
                setOrder(orderData);

                console.log('Henting ordrelinjer for orderNo:', orderNo);
                try {
                    orderLines = await pOrderLineService.getOrderLines(orderNo);
                } catch (lineError) {
                    console.warn('Kunne ikke hente ordrelinjer direkte, prøver via details:', lineError);
                    try {
                        const orderDetails = await pOrderLineService.getOrderDetails(orderNo);
                        orderLines = orderDetails.lines;
                    } catch (detailsError) {
                        throw new Error(`Kunne ikke hente ordrelinjer: ${lineError instanceof Error ? lineError.message : 'Ukjent feil'}`);
                    }
                }

                if (!orderLines || orderLines.length === 0) {
                    console.warn('Ingen ordrelinjer funnet for ordre', orderNo);
                    setReceivingLines([]);
                    setLoading(false);
                    return;
                }

                usedBatchId = await stcTransService.getOrCreateBatchId(orderNo);
            } else {
                throw new Error('Verken orderNo, inboundShipmentNo eller bilmottakBatchId er spesifisert');
            }
            setBatchId(usedBatchId);

            // For bilmottak mode, load existing transactions directly (no order lines preloaded)
            if (receivingMode === 'bilmottak' && orderLines.length === 0) {
                console.log('Bilmottak mode: Loading existing transactions from batch', usedBatchId);
                try {
                    const transactions = await stcTransService.getTransactionsByBatch(usedBatchId);
                    console.log(`Bilmottak: Loaded ${transactions.length} existing transactions`);

                    // Group transactions by orderNo/lineNo to create virtual receiving lines
                    const groupedTransactions = new Map<string, StcTransDto[]>();
                    transactions.forEach(trans => {
                        const key = `${trans.orderNo}-${trans.lineNo}`;
                        if (!groupedTransactions.has(key)) {
                            groupedTransactions.set(key, []);
                        }
                        groupedTransactions.get(key)!.push(trans);
                    });

                    // Create receiving lines from grouped transactions
                    const bilmottakLines: ReceivingLine[] = [];
                    groupedTransactions.forEach((groupTransactions, key) => {
                        const firstTrans = groupTransactions[0];
                        const activeTransactions = groupTransactions.filter(t => t.lineStatus < 50);
                        const stcTransReceived = activeTransactions.reduce((sum, t) => sum + (t.quantity || 0), 0);

                        bilmottakLines.push({
                            orderLine: {
                                id: 0,
                                companyNo: firstTrans.companyNo,
                                orderNo: firstTrans.orderNo || 0,
                                lineNo: firstTrans.lineNo || 0,
                                itemNo: firstTrans.itemNo,
                                description1: firstTrans.description1 || '',
                                quantity: firstTrans.quantityOrigin || firstTrans.quantity || 0,
                                quantityDelivered: 0,
                                itemUnitCode: firstTrans.itemUnitCode || '',
                                itemUnitTxt: firstTrans.itemUnitTxt || '',
                                warehouse: firstTrans.warehouse || '',
                                itemBarCode: firstTrans.itemBarCode || '',
                            } as POrderLineDto,
                            receivedTransactions: groupTransactions,
                            totalReceived: stcTransReceived,
                            remainingQuantity: (firstTrans.quantityOrigin || 0) - stcTransReceived
                        });
                    });

                    setReceivingLines(bilmottakLines);
                    setHasTransactions(transactions.length > 0);

                    // Check if we can finish receiving
                    if (transactions.length > 0) {
                        const hasArrivedTransactions = transactions.some(t => t.lineStatus === 20);
                        if (hasArrivedTransactions) {
                            await checkCanFinishReceiving(usedBatchId);
                        }
                    }

                    setLoading(false);
                    return; // Early return for bilmottak mode
                } catch (err) {
                    console.warn('Could not load bilmottak transactions:', err);
                    setReceivingLines([]);
                    setHasTransactions(false);
                    setLoading(false);
                    return;
                }
            }

            const receivingLineData: ReceivingLine[] = await Promise.all(
                orderLines.map(async (line) => {
                    try {
                        const transactions = await stcTransService.getTransactionsByBatch(usedBatchId, bustCache);
                        console.log(`Loaded ${transactions.length} transactions for batch ${usedBatchId}${bustCache ? ' (cache busted)' : ''}`);
                        // Hent ALLE transaksjoner (inkludert låste med status >= 50)
                        // Filter by both orderNo AND lineNo to avoid duplicates in shipment mode (multiple orders can have same lineNo)
                        const lineTransactions = transactions.filter(t => t.orderNo === line.orderNo && t.lineNo === line.lineNo);
                        console.log(`Order ${line.orderNo} Line ${line.lineNo}: found ${lineTransactions.length} transactions`, lineTransactions.map(t => ({ id: t.id, qty: t.quantity, batch: t.itemBatchNo })));
                        // For beregning av "mottatt" teller vi kun aktive transaksjoner (status < 50)
                        const activeLineTransactions = lineTransactions.filter(t => t.lineStatus < 50);
                        const stcTransReceived = activeLineTransactions.reduce((sum, t) => sum + (t.quantity || 0), 0);
                        
                        // Total mottatt = tidligere ferdigstillte mottak + aktive StcTrans
                        const totalReceived = (line.quantityDelivered || 0) + stcTransReceived;
                        
                        // Gjenværende = bestilt - total mottatt
                        const remainingQuantity = (line.quantity || 0) - totalReceived;

                        return {
                            orderLine: line,
                            receivedTransactions: lineTransactions,
                            totalReceived,
                            remainingQuantity
                        };
                    } catch (err) {
                        console.log('Ingen eksisterende mottakslinjer funnet for batch-ID:', usedBatchId);
                        
                        // Fallback når ingen StcTrans finnes - bruk kun ordrelinje data
                        const totalReceived = line.quantityDelivered || 0;
                        const remainingQuantity = (line.quantity || 0) - totalReceived;
                        
                        return {
                            orderLine: line,
                            receivedTransactions: [],
                            totalReceived,
                            remainingQuantity
                        };
                    }
                })
            );

            setReceivingLines(receivingLineData);
            setAvailableOrderLines(orderLines);
            
            const totalTransactions = receivingLineData.reduce((sum, line) => sum + line.receivedTransactions.length, 0);
            setHasTransactions(totalTransactions > 0);

            // Sjekk om det finnes minst én transaksjon med LineStatus = 20 (Ankommet/UnderArbeid)
            const hasArrivedTransactions = receivingLineData.some(line =>
                line.receivedTransactions.some(trans => Number(trans.lineStatus) === StcTransLineStatus.UnderArbeid)
            );

            if (totalTransactions > 0) {
                // Start mottak hvis ikke allerede startet
                try {
                    await stcTransService.startReceiving(usedBatchId);
                } catch (startError) {
                    console.log('Could not start receiving (may already be started):', startError);
                }

                // Aktiver ferdigstill-knappene hvis det finnes ankomne linjer (LineStatus = 20)
                if (hasArrivedTransactions) {
                    // Valider via backend for å sikre at alle krav er oppfylt (bins, batch-numre, etc.)
                    await checkCanFinishReceiving(usedBatchId);
                } else {
                    setCanFinishReceiving(false);
                }
            } else {
                setCanFinishReceiving(false);
            }
            
            setLoading(false);
        } catch (err) {
            console.error('Detaljert feil ved henting av ordre-data:', err);
            setError(`Kunne ikke hente ordre-informasjon: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
            setLoading(false);
        }
    };

    /**
     * Helper function that performs the actual save operation.
     * Extracted to allow pre-confirmation via modal.
     */
    const proceedWithSave = async (
        lineNo: number,
        quantity: number,
        batchNo?: string,
        expDate?: string,
        receivingLine?: ReceivingLine,
        receivingLocation?: string
    ) => {
        if (!order) return;

        try {
            if (!receivingLine) return;

            // Check if we're editing an existing StcTrans or creating a new one
            if (editingStcTrans) {
                // Update existing StcTrans
                const updateRequest = buildStcTransUpdateRequest(editingStcTrans, {
                    quantity,
                    itemBatchNo: batchNo?.trim() || editingStcTrans.itemBatchNo || undefined,
                    itemBatchExpDate: expDate ? new Date(expDate) : editingStcTrans.itemBatchExpDate
                });

                await updateTransactionWithFallback(
                    stcTransService,
                    editingStcTrans.batchID,
                    editingStcTrans.lineID,
                    updateRequest
                );
            } else {
                // Create new StcTrans
                await stcTransService.ensureTransOrderExists(batchId, order);

                // Use passed receivingLocation or fall back to profile default
                let locationName = receivingLocation || '';
                if (!locationName) {
                    try {
                        const profile = await userProfileService.getCurrentUserProfile();
                        locationName = profile.defaultGRBinCode || '';
                    } catch (profileError) {
                        console.warn('Kunne ikke hente profil for LocationName:', profileError);
                    }
                }

                let itemType: number | undefined;
                try {
                    const item = await itemsService.getItemByCompanyAndItemNo(
                        order.companyNo,
                        receivingLine.orderLine.itemNo
                    );
                    itemType = item.itemType;
                } catch (itemError) {
                    console.warn('Kunne ikke hente item for ItemType:', itemError);
                }

                const erpLinkName = order.orderNoERP + '-' + receivingLine.orderLine.lineNoERP;

                const newTransaction: CreateStcTransRequest = {
                    batchID: batchId,
                    lineType: 0,
                    orderNo: receivingLine.orderLine.orderNo, // Use line's orderNo for shipment mode support
                    lineNo: lineNo,
                    locationName: locationName,
                    quantity: quantity,
                    transDt: new Date(),
                    warehouse: order.warehouse || 'MAIN',
                    lineStatus: StcTransLineStatus.UnderArbeid,
                    itemNo: receivingLine.orderLine.itemNo,
                    itemBarCode: receivingLine.orderLine.itemBarCode,
                    description1: receivingLine.orderLine.description1,
                    itemUnitCode: receivingLine.orderLine.itemUnitCode,
                    itemUnitTxt: receivingLine.orderLine.itemUnitTxt,
                    transType: 0,
                    orderNoERP: order.orderNoERP,
                    lineNoERP: receivingLine.orderLine.lineNoERP,
                    erpLinkName: erpLinkName,
                    itemType: itemType,
                    itemBatchNo: batchNo?.trim() || undefined,
                    itemBatchExpDate: expDate ? new Date(expDate) : undefined
                };

                console.log('Creating new StcTrans with batch info:', { batchNo, expDate });
                await stcTransService.createTransaction(batchId, newTransaction);
            }

            // Clear all editing states FIRST to prevent stale state conflicts
            console.log('Clearing editing states before reload');
            setNewReceipt(null);
            setSelectedSearchLine(null);
            setEditingStcTrans(null);
            setBatchEditState({}); // Reset batch edit state to force re-initialization from fresh data
            setWarningMessage('');
            setValidationError('');
            setValidationWarning('');
            setError(null);

            // Reload data to refresh the list with updated values
            console.log('Reloading order data after save with cache busting');

            // Add delay to ensure database transaction is committed and events processed
            await new Promise(resolve => setTimeout(resolve, 300));

            // Force cache bust to ensure we get fresh data
            await loadOrderData(true);

            console.log('Order data reloaded, receivingLines should now show updated values');

            // Re-check if receiving can be finished after adding/updating receipt
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Error adding/updating receipt:', err);
            setError(t('warehouse.modules.receipt.errors.saveReceiptFailed', { error: err instanceof Error ? err.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke lagre mottak: ${err instanceof Error ? err.message : 'Ukjent feil'}`));
        }
    };

    const handleAddReceipt = async (lineNo: number, quantity: number, batchNo?: string, expDate?: string, receivingLocation?: string) => {
        if (!order) return;

        // Validate quantity is positive
        if (quantity <= 0) {
            setError(t('warehouse.modules.receipt.validation.quantityMustBePositive', {}, 'Antall må være større enn 0'));
            return;
        }

        // Validate batch fields (safety check - ReceivingActiveLine should catch this first)
        const itemType = selectedLineItemType;
        if ((itemType === 1 || itemType === 5) && !batchNo?.trim()) {
            setError('Batchnummer er påkrevd for denne varen');
            return;
        }
        if (itemType === 4 && !expDate) {
            setError('Utløpsdato er påkrevd for denne varen');
            return;
        }

        // Validate over-receiving - use selectedSearchLine's orderNo for composite key lookup
        const receivingLine = findReceivingLine(lineNo, selectedSearchLine?.orderNo);
        const orderLine = receivingLine?.orderLine;

        if (orderLine) {
            // When editing, get the ORIGINAL transaction quantity from server data (receivedTransactions),
            // not from editingStcTrans which gets updated live as the user types
            const editingQty = editingStcTrans
                ? (receivingLine?.receivedTransactions.find(t => t.lineID === editingStcTrans.lineID)?.quantity || 0)
                : undefined;
            const validation = validateOverReceiving(orderLine, quantity, receivingLine, editingQty);
            console.log('🔍 Validation result:', validation);

            if (!validation.allowed) {
                console.log('🚫 Blocking save - setting error');
                // BLOCKED - Show error inline and return
                setValidationError(validation.message);
                setValidationWarning('');
                return;
            } else if (validation.isWarning) {
                console.log('⚠️ Showing warning modal - waiting for user confirmation');
                // WARNING - Show confirmation modal BEFORE proceeding
                setOverReceiveModalMessage(validation.message);
                setPendingOverReceiveAction(() => async () => {
                    // This function will be called if user clicks "Fortsett"
                    await proceedWithSave(lineNo, quantity, batchNo, expDate, receivingLine, receivingLocation);
                });
                setShowOverReceiveConfirmModal(true);
                return; // Stop here, wait for user decision
            } else {
                console.log('✅ No issues - clearing messages');
                // Clear any previous warnings
                setValidationError('');
                setValidationWarning('');
            }
        }

        // Check if this is a serial number item (ItemType=6) - requires serial number registration
        if (selectedLineItemType === 6 && !editingStcTrans && quantity > 0) {
            setSerialNumberPendingData({
                lineNo,
                quantity,
                batchNo,
                expDate,
                receivingLocation,
                action: 'save',
                itemNo: selectedSearchLine?.itemNo || '',
                itemDescription: selectedSearchLine?.description1 || ''
            });
            setShowSerialNumberModal(true);
            return;
        }

        // If we get here, validation passed without warning - proceed directly
        await proceedWithSave(lineNo, quantity, batchNo, expDate, receivingLine, receivingLocation);
    };

    const handleDeleteReceipt = async (lineID: number, transactionId: number) => {
        try {
            await stcTransService.deleteTransaction(batchId, lineID);
            await loadOrderData();

            // Clear active line state to return to main view
            setNewReceipt(null);
            setSelectedSearchLine(null);
            setEditingStcTrans(null);
            setValidationError('');
            setValidationWarning('');
            setError(null);

            // Re-check if receiving can be finished after deleting receipt
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Error deleting receipt:', err);
            setError(`Kunne ikke slette mottak: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
        }
    };

    const handlePutAway = (stcTrans: StcTransDto) => {
        setSelectedStcTrans(stcTrans);
        setShowPutAwayModal(true);
    };

    const handlePutAwayAll = () => {
        // For now, we'll use the first available StcTrans as a representative transaction
        // This could be enhanced to handle multiple transactions or create a batch put-away
        const allTransactions = receivingLines.flatMap(line => line.receivedTransactions);
        if (allTransactions.length > 0) {
            setSelectedStcTrans(allTransactions[0]); // Use first transaction as representative
            setShowPutAwayModal(true);
        }
    };

    const handleEditStcTrans = (stcTrans: StcTransDto) => {
        setSelectedStcTrans(stcTrans);
        setShowEditModal(true);
    };

    const handleDirectReceipt = (stcTrans: StcTransDto) => {
        setSelectedStcTrans(stcTrans);
        setShowDirectReceiptModal(true);
    };

    /**
     * Helper function that performs the actual direct receipt creation.
     * Extracted to allow pre-confirmation via modal.
     */
    const proceedWithDirectReceipt = async (
        lineNo: number,
        quantity: number,
        batchNo?: string,
        expDate?: string,
        receivingLine?: ReceivingLine,
        receivingLocation?: string
    ) => {
        if (!order) return;

        try {
            if (!receivingLine) {
                setError('Finner ikke ordrelinje');
                return;
            }

            // Valider batch-info hvis nødvendig (ItemType 1 eller 4)
            let itemType: number | undefined;
            try {
                const item = await itemsService.getItemByCompanyAndItemNo(
                    order.companyNo,
                    receivingLine.orderLine.itemNo
                );
                itemType = item.itemType;

                // Valider batch-nummer for ItemType 1/5 (påkrevd), 4 (valgfri - auto-genereres)
                if ((itemType === 1 || itemType === 5) && !batchNo) {
                    setError('Batchnummer er påkrevd for denne varen');
                    return;
                }

                // Valider utløpsdato for ItemType 4 (påkrevd)
                if (itemType === 4 && !expDate) {
                    setError('Utløpsdato er påkrevd for denne varen');
                    return;
                }
            } catch (itemError) {
                console.warn('Kunne ikke hente item for ItemType-validering:', itemError);
            }

            // Opprett StcTrans først (samme logikk som handleAddReceipt)
            await stcTransService.ensureTransOrderExists(batchId, order);

            // Use passed receivingLocation or fall back to profile default
            let locationName = receivingLocation || '';
            if (!locationName) {
                try {
                    const profile = await userProfileService.getCurrentUserProfile();
                    locationName = profile.defaultGRBinCode || '';
                } catch (profileError) {
                    console.warn('Kunne ikke hente profil for LocationName:', profileError);
                }
            }

            const erpLinkName = order.orderNoERP + '-' + receivingLine.orderLine.lineNoERP;

            const newTransaction: CreateStcTransRequest = {
                batchID: batchId,
                lineType: 0,
                orderNo: receivingLine.orderLine.orderNo, // Use line's orderNo for shipment mode support
                lineNo: lineNo,
                locationName: locationName,
                quantity: quantity,
                transDt: new Date(),
                warehouse: order.warehouse || 'MAIN',
                lineStatus: StcTransLineStatus.UnderArbeid,
                itemNo: receivingLine.orderLine.itemNo,
                itemBarCode: receivingLine.orderLine.itemBarCode,
                description1: receivingLine.orderLine.description1,
                itemUnitCode: receivingLine.orderLine.itemUnitCode,
                itemUnitTxt: receivingLine.orderLine.itemUnitTxt,
                transType: 0,
                orderNoERP: order.orderNoERP,
                lineNoERP: receivingLine.orderLine.lineNoERP,
                erpLinkName: erpLinkName,
                itemType: itemType,
                itemBatchNo: batchNo,
                itemBatchExpDate: expDate ? new Date(expDate) : undefined
            };

            console.log('Oppretter StcTrans for direktemottak:', newTransaction);
            const createdStcTrans = await stcTransService.createTransaction(batchId, newTransaction);

            console.log('StcTrans opprettet, åpner direktemottak-modal');

            // Nå åpne direktemottak-modalen med den nyopprettede StcTrans
            setSelectedStcTrans(createdStcTrans);
            setShowDirectReceiptModal(true);

            // Clear aktiv linje state and messages
            setNewReceipt(null);
            setSelectedSearchLine(null);
            setWarningMessage('');
            setValidationError('');
            setValidationWarning('');
            setError(null);

        } catch (err) {
            console.error('Error creating StcTrans for direct receipt:', err);
            setError(`Kunne ikke opprette mottak: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
        }
    };

    /**
     * Håndterer direktemottak fra "Aktiv linje" når vi oppretter NYE mottak.
     * Følger legacy-pattern: Opprett StcTrans først, deretter åpne direktemottak-modal.
     */
    const handleDirectReceiptFromNew = async (lineNo: number, quantity: number, batchNo?: string, expDate?: string, receivingLocation?: string) => {
        if (!order || !newReceipt) return;

        setError(null);

        const receivingLine = findReceivingLine(lineNo, selectedSearchLine?.orderNo);
        if (!receivingLine) {
            setError('Finner ikke ordrelinje');
            return;
        }

        // Valider antall
        if (quantity <= 0) {
            setError(t('warehouse.modules.receipt.validation.quantityMustBePositive', {}, 'Antall må være større enn 0'));
            return;
        }

        // Validate over-receiving - get ORIGINAL quantity from server data, not live-updated editingStcTrans
        const editingQty = editingStcTrans
            ? (receivingLine?.receivedTransactions.find(t => t.lineID === editingStcTrans.lineID)?.quantity || 0)
            : undefined;
        const validation = validateOverReceiving(receivingLine.orderLine, quantity, receivingLine, editingQty);
        console.log('🔍 Direct Receipt Validation result:', validation);

        if (!validation.allowed) {
            console.log('🚫 Blocking direct receipt - setting error');
            // BLOCKED - Show error inline and return
            setValidationError(validation.message);
            setValidationWarning('');
            return;
        } else if (validation.isWarning) {
            console.log('⚠️ Showing warning modal for direct receipt - waiting for user confirmation');
            // WARNING - Show confirmation modal BEFORE proceeding
            setOverReceiveModalMessage(validation.message);
            setPendingOverReceiveAction(() => async () => {
                // This function will be called if user clicks "Fortsett"
                await proceedWithDirectReceipt(lineNo, quantity, batchNo, expDate, receivingLine, receivingLocation);
            });
            setShowOverReceiveConfirmModal(true);
            return; // Stop here, wait for user decision
        } else {
            console.log('✅ No issues - clearing messages');
            // Clear any previous warnings
            setValidationError('');
            setValidationWarning('');
        }

        // Check if this is a serial number item (ItemType=6) - requires serial number registration
        if (selectedLineItemType === 6 && quantity > 0) {
            setSerialNumberPendingData({
                lineNo,
                quantity,
                batchNo,
                expDate,
                receivingLocation,
                action: 'directReceipt',
                itemNo: selectedSearchLine?.itemNo || '',
                itemDescription: selectedSearchLine?.description1 || ''
            });
            setShowSerialNumberModal(true);
            return;
        }

        // If we get here, validation passed without warning - proceed directly
        await proceedWithDirectReceipt(lineNo, quantity, batchNo, expDate, receivingLine, receivingLocation);
    };

    const handleModalClose = () => {
        setShowPutAwayModal(false);
        setShowEditModal(false);
        setShowDirectReceiptModal(false);
        setSelectedStcTrans(null);
    };

    const handleModalSuccess = async () => {
        await loadOrderData();
        handleModalClose();

        // Clear active line state to return to main view
        setNewReceipt(null);
        setSelectedSearchLine(null);
        setEditingStcTrans(null);
        setValidationError('');
        setValidationWarning('');
        setError(null);

        // Re-check if receiving can be finished after modal operations
        if (batchId) {
            await checkCanFinishReceiving(batchId);
        }
    };

    const handleConfirmOverReceive = async () => {
        console.log('✅ User confirmed over-receive');
        setShowOverReceiveConfirmModal(false);

        if (pendingOverReceiveAction) {
            await pendingOverReceiveAction();
            setPendingOverReceiveAction(null);
        }
        setOverReceiveModalMessage('');
    };

    const handleCancelOverReceive = () => {
        console.log('❌ User cancelled over-receive');
        setShowOverReceiveConfirmModal(false);
        setPendingOverReceiveAction(null);
        setOverReceiveModalMessage('');
        // Keep data intact - user can modify quantity and retry
    };

    /**
     * Handles completion of serial number registration (ItemType=6).
     * Creates one StcTrans per serial number, each with quantity=1.
     */
    const handleSerialNumberComplete = async (serialNumbers: string[]) => {
        setShowSerialNumberModal(false);

        if (!serialNumberPendingData || !order) {
            setError('Mangler data for serienummerregistrering');
            return;
        }

        const { lineNo, batchNo, expDate, receivingLocation, action } = serialNumberPendingData;

        try {
            await stcTransService.ensureTransOrderExists(batchId, order);

            // Determine receiving location
            let locationName = receivingLocation || '';
            if (!locationName) {
                try {
                    const profile = await userProfileService.getCurrentUserProfile();
                    locationName = profile.defaultGRBinCode || '';
                } catch (profileError) {
                    console.warn('Kunne ikke hente profil for LocationName:', profileError);
                }
            }

            const receivingLine = findReceivingLine(lineNo, selectedSearchLine?.orderNo);
            const orderLine = receivingLine?.orderLine;
            if (!orderLine) {
                setError('Finner ikke ordrelinje');
                setSerialNumberPendingData(null);
                return;
            }

            const erpLinkName = order.orderNoERP + '-' + orderLine.lineNoERP;

            // Create one StcTrans per serial number, each with quantity=1
            const transactions: CreateStcTransRequest[] = serialNumbers.map(serialNo => ({
                batchID: batchId,
                lineType: 0,
                orderNo: orderLine.orderNo,
                lineNo: lineNo,
                locationName: locationName,
                quantity: 1,
                transDt: new Date(),
                warehouse: order.warehouse || 'MAIN',
                lineStatus: StcTransLineStatus.UnderArbeid,
                itemNo: orderLine.itemNo,
                itemBarCode: orderLine.itemBarCode,
                description1: orderLine.description1,
                itemUnitCode: orderLine.itemUnitCode,
                itemUnitTxt: orderLine.itemUnitTxt,
                transType: 0,
                orderNoERP: order.orderNoERP,
                lineNoERP: orderLine.lineNoERP,
                erpLinkName: erpLinkName,
                itemType: 6,
                itemSerialNo: serialNo,
                itemBatchNo: batchNo?.trim() || undefined,
                itemBatchExpDate: expDate ? new Date(expDate) : undefined
            }));

            console.log(`Oppretter ${transactions.length} serienummer-transaksjoner`);
            const createdTransactions = await stcTransService.createTransactionsBulk(batchId, transactions);

            // Clear editing states
            setNewReceipt(null);
            setSelectedSearchLine(null);
            setEditingStcTrans(null);
            setBatchEditState({});
            setWarningMessage('');
            setValidationError('');
            setValidationWarning('');
            setError(null);
            setSerialNumberPendingData(null);

            // Reload data
            await new Promise(resolve => setTimeout(resolve, 300));
            await loadOrderData(true);

            // If direct receipt was requested, open DirectReceiptModal for the first created transaction
            if (action === 'directReceipt' && createdTransactions.length > 0) {
                setSelectedStcTrans(createdTransactions[0]);
                setShowDirectReceiptModal(true);
            }

            // Re-check finish status
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Feil ved opprettelse av serienummer-transaksjoner:', err);
            setError(`Kunne ikke opprette serienummer-mottak: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
            setSerialNumberPendingData(null);
        }
    };

    const initializeBatchEdit = (trans: StcTransDto) => {
        const transId = `${trans.id}`;
        if (!batchEditState[transId]) {
            setBatchEditState(prev => ({
                ...prev,
                [transId]: {
                    itemBatchNo: trans.itemBatchNo || '',
                    itemBatchExpDate: trans.itemBatchExpDate ? new Date(trans.itemBatchExpDate).toISOString().split('T')[0] : '',
                    quantity: trans.quantity || 0, // Bruk faktisk quantity fra StcTrans
                    isEditing: false,
                    isSaving: false,
                    isEditingQuantity: false
                }
            }));
        }
    };
    
    const handleBatchFieldChange = (trans: StcTransDto, field: 'itemBatchNo' | 'itemBatchExpDate', value: string) => {
        const transId = `${trans.id}`;
        setBatchEditState(prev => ({
            ...prev,
            [transId]: {
                ...prev[transId],
                [field]: value,
                isEditing: true
            }
        }));
    };
    
    const handleQuantityChange = (trans: StcTransDto, value: number) => {
        const transId = `${trans.id}`;
        setBatchEditState(prev => ({
            ...prev,
            [transId]: {
                ...prev[transId],
                quantity: value,
                isEditingQuantity: true
            }
        }));
    };
    
    const handleQuantitySave = async (trans: StcTransDto) => {
        const transId = `${trans.id}`;
        const editState = batchEditState[transId];
        
        if (!editState || !editState.isEditingQuantity) {
            return; // Ingen endringer å lagre
        }
        
        if (editState.quantity <= 0) {
            setError(t('warehouse.modules.receipt.validation.quantityMustBePositive', {}, 'Antall må være større enn 0'));
            return;
        }
        
        try {
            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: true }
            }));
            
            // Use the helper to build update request and handle fallback
            const updateRequest = buildStcTransUpdateRequest(trans, {
                quantity: editState.quantity
            });

            await updateTransactionWithFallback(stcTransService, trans.batchID, trans.lineID, updateRequest);
            
            // Mark as saved
            setBatchEditState(prev => ({
                ...prev,
                [transId]: {
                    ...prev[transId],
                    isEditingQuantity: false,
                    isSaving: false
                }
            }));

            // Reload data to show changes
            await loadOrderData();

            // Re-check if receiving can be finished after updating quantity
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Error updating transaction quantity:', err);
            setError(t('warehouse.modules.receipt.errors.updateQuantityFailed', { error: err instanceof Error ? err.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke oppdatere antall: ${err instanceof Error ? err.message : 'Ukjent feil'}`));

            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: false }
            }));
        }
    };

    const handleSaveAllChanges = async (trans: StcTransDto) => {
        const transId = `${trans.id}`;
        const editState = batchEditState[transId];
        
        if (!editState || (!editState.isEditing && !editState.isEditingQuantity)) {
            return; // Ingen endringer å lagre
        }
        
        try {
            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: true }
            }));
            
            // Valider påkrevde felt før lagring
            if ((trans.itemType === 1 || trans.itemType === 5) && !editState.itemBatchNo.trim()) {
                setError(t('warehouse.modules.receipt.validation.batchRequiredForItemType', {}, 'Batchnummer er påkrevd for denne varetypen'));
                setBatchEditState(prev => ({
                    ...prev,
                    [transId]: { ...prev[transId], isSaving: false }
                }));
                return;
            }
            
            if (trans.itemType === 4 && !editState.itemBatchExpDate) {
                setError(t('warehouse.modules.receipt.validation.expiryRequiredForItemType', {}, 'Utgått dato er påkrevd for denne varetypen'));
                setBatchEditState(prev => ({
                    ...prev,
                    [transId]: { ...prev[transId], isSaving: false }
                }));
                return;
            }
            
            if (editState.quantity <= 0) {
                setError(t('warehouse.modules.receipt.validation.quantityMustBePositive', {}, 'Antall må være større enn 0'));
                setBatchEditState(prev => ({
                    ...prev,
                    [transId]: { ...prev[transId], isSaving: false }
                }));
                return;
            }
            
            // Use the helper to build update request and handle fallback
            const updateRequest = buildStcTransUpdateRequest(trans, {
                quantity: editState.quantity,
                itemBatchNo: editState.itemBatchNo.trim() || undefined,
                itemBatchExpDate: editState.itemBatchExpDate ? new Date(editState.itemBatchExpDate) : undefined
            });

            await updateTransactionWithFallback(stcTransService, trans.batchID, trans.lineID, updateRequest);
            
            // Mark as saved
            setBatchEditState(prev => ({
                ...prev,
                [transId]: {
                    ...prev[transId],
                    isEditing: false,
                    isEditingQuantity: false,
                    isSaving: false
                }
            }));

            // Reload data to show changes
            await loadOrderData();

            // Re-check if receiving can be finished after saving all changes
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Error saving all changes:', err);
            setError(t('warehouse.modules.receipt.errors.saveChangesFailed', { error: err instanceof Error ? err.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke lagre endringer: ${err instanceof Error ? err.message : 'Ukjent feil'}`));

            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: false }
            }));
        }
    };

    const handleBatchFieldSave = async (trans: StcTransDto, field: 'itemBatchNo' | 'itemBatchExpDate') => {
        const transId = `${trans.id}`;
        const editState = batchEditState[transId];
        
        if (!editState || !editState.isEditing) {
            return; // Ingen endringer å lagre
        }
        
        // Valider påkrevde felt
        if ((trans.itemType === 1 || trans.itemType === 5) && !editState.itemBatchNo.trim()) {
            setError('Batchnummer er påkrevd for denne varetypen');
            return;
        }

        if (trans.itemType === 4 && !editState.itemBatchExpDate) {
            setError('Utløpsdato er påkrevd for denne varetypen');
            return;
        }
        
        try {
            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: true }
            }));
            
            // Prøv å bruke den nye updateBatchInfo metoden først
            try {
                await stcTransService.updateBatchInfo(trans.batchID, trans.lineID, {
                    itemBatchNo: editState.itemBatchNo.trim() || undefined,
                    itemBatchExpDate: editState.itemBatchExpDate ? new Date(editState.itemBatchExpDate) : undefined
                });

                console.log('Batch info updated successfully using PATCH method');

            } catch (updateError: any) {
                // Hvis PATCH ikke er støttet, fall tilbake til delete/create
                if (updateError.message === 'UPDATE_NOT_SUPPORTED') {
                    console.log('PATCH not supported, falling back to delete/create method');

                    const updateRequest = buildStcTransUpdateRequest(trans, {
                        itemBatchNo: editState.itemBatchNo.trim() || undefined,
                        itemBatchExpDate: editState.itemBatchExpDate ? new Date(editState.itemBatchExpDate) : undefined
                    });

                    // Delete old transaction and create new one with updated info
                    await stcTransService.deleteTransaction(trans.batchID, trans.lineID);
                    await stcTransService.createTransaction(trans.batchID, updateRequest);

                    console.log('Batch info updated using fallback delete/create method');
                } else {
                    throw updateError;
                }
            }
            
            // Mark as saved
            setBatchEditState(prev => ({
                ...prev,
                [transId]: {
                    ...prev[transId],
                    isEditing: false,
                    isSaving: false
                }
            }));

            // Reload data to show changes
            await loadOrderData();

            // Re-check if receiving can be finished after updating batch info
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Error updating batch info:', err);
            setError(t('warehouse.modules.receipt.errors.updateBatchInfoFailed', { error: err instanceof Error ? err.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke oppdatere batch-informasjon: ${err instanceof Error ? err.message : 'Ukjent feil'}`));

            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: false }
            }));
        }
    };

    const handlePrintLabel = async (reportId: number | null, numLabels: number) => {
        if (!selectedSearchLine) {
            setError(t('warehouse.modules.receipt.errors.noItemSelected', {}, 'Ingen vare valgt'));
            return;
        }

        if (!order) {
            setError(t('warehouse.modules.receipt.errors.orderNotLoaded', {}, 'Ordre ikke lastet'));
            return;
        }

        try {
            setError(null);
            const response = await printingService.printLabel(order.companyNo, {
                reportID: reportId,
                numOfLabels: numLabels,
                itemNo: selectedSearchLine.itemNo,
                itemUnitCode: selectedSearchLine.itemUnitCode,
                orderNo: order.orderNo,
                lineNo: selectedSearchLine.lineNo,
                // Add batch info if available (from editingStcTrans or newReceipt)
                batchNo: editingStcTrans?.itemBatchNo,
                expiryDate: editingStcTrans?.itemBatchExpDate
                    ? new Date(editingStcTrans.itemBatchExpDate).toISOString().split('T')[0]
                    : undefined,
                quantity: editingStcTrans?.quantity || newReceipt?.quantity
            });

            if (response.success) {
                // Success - you could show a success message in a success state if needed
                console.log(`${response.labelsProcessed || numLabels} etikett(er) skrevet ut`);
            } else {
                setError(response.message || t('warehouse.modules.receipt.errors.printLabelFailed', {}, 'Feil ved utskrift av etikett'));
            }
        } catch (error: any) {
            console.error('Error printing label:', error);
            setError(error.message || t('warehouse.modules.receipt.errors.printLabelFailed', {}, 'Feil ved utskrift av etikett'));
        }
    };

    const handleStcTransClick = (trans: StcTransDto, orderLine: POrderLineDto) => {
        // Set editing mode for existing StcTrans
        setEditingStcTrans(trans);
        setSelectedSearchLine(orderLine);
        setNewReceipt({
            lineNo: orderLine.lineNo,
            quantity: trans.quantity || 0,
            orderNo: orderLine.orderNo
        });
    };

    // Enhanced state for batch and expiry from barcode
    const [scannedBatchInfo, setScannedBatchInfo] = useState<{ batchNo?: string; expDate?: string }>({});

    // Detect if any modal is open to disable global scanning
    const isAnyModalOpen = showPutAwayModal || showEditModal || showDirectReceiptModal ||
                           showLineBrowser || showAdvancedConfigModal || isBulkProcessing || showSerialNumberModal;

    // Check if global barcode scanning is enabled via system setting
    const barcodeScannerEnabled = useBarcodeScannerEnabled();

    // Global barcode scanner - processes scans without requiring input focus
    useSimpleBarcodeScanner({
        enabled: barcodeScannerEnabled && !isAnyModalOpen,
        onScan: (barcode: string) => {
            processGlobalBarcode(barcode);
        }
    });

    /**
     * Find the correct receiving line by orderNo + lineNo composite key.
     * In bilmottak/shipment mode, multiple orders can have the same lineNo,
     * so we must match on both orderNo AND lineNo.
     */
    const findReceivingLine = (lineNo: number, orderNoForLine?: number): ReceivingLine | undefined => {
        if (orderNoForLine !== undefined) {
            // Try composite key first (orderNo + lineNo)
            const match = receivingLines.find(rl => rl.orderLine.orderNo === orderNoForLine && rl.orderLine.lineNo === lineNo);
            if (match) return match;
        }
        // Fallback to lineNo-only (single-order modes like Modus 1)
        return receivingLines.find(rl => rl.orderLine.lineNo === lineNo);
    };

    /**
     * Calculate remaining quantity for a line based on transaction status
     * Remaining = Ordered - PreviouslyDelivered - CurrentBatchReceived - Arrived
     * Accounts for both previous batch deliveries (quantityDelivered) and current batch transactions
     */
    const calculateRemainingQuantity = (line: POrderLineDto, lineNo: number): number => {
        const receivingLine = findReceivingLine(lineNo, line.orderNo);

        if (!receivingLine) {
            // Fallback to backend's remainingQuantity if we don't have transaction data
            return line.remainingQuantity || 0;
        }

        // quantityDelivered is authoritative for all completed deliveries (status >= 50).
        // Only add arrivedQuantity (status 20) which are in-progress, not yet in quantityDelivered.
        const deliveredQty = line.quantityDelivered || 0;

        // Calculate arrivedQuantity (status 20 - UnderArbeid/Ankommet)
        const arrivedQuantity = receivingLine.receivedTransactions
            .filter(t => t.lineStatus === StcTransLineStatus.UnderArbeid)
            .reduce((sum, t) => sum + (t.quantity || 0), 0);

        // Calculate remaining: Ordered - Delivered - Arrived
        return (line.quantity || 0) - deliveredQty - arrivedQuantity;
    };

    /**
     * Validate if receiving quantity would exceed ordered quantity
     * Returns: { allowed: boolean, message: string, isWarning: boolean }
     */
    const validateOverReceiving = (
        orderLine: POrderLineDto,
        newQuantity: number,
        receivingLine?: ReceivingLine,
        editingTransactionQty?: number
    ): { allowed: boolean; message: string; isWarning: boolean } => {
        console.log('🔍 validateOverReceiving called');
        console.log('  - orderLine:', orderLine);
        console.log('  - newQuantity:', newQuantity);
        console.log('  - receivingLine:', receivingLine);
        console.log('  - editingTransactionQty:', editingTransactionQty);

        const orderedQty = orderLine.quantity || 0;
        console.log('  - orderedQty:', orderedQty);

        // quantityDelivered is the authoritative source for all completed deliveries (status >= 50).
        // It already includes quantities from StcTrans with status >= 50, so we must NOT count those again.
        // We only add arrivedQuantity (status 20) which are in-progress receipts not yet in quantityDelivered.
        const deliveredQty = orderLine.quantityDelivered || 0;
        console.log('  - deliveredQty (quantityDelivered):', deliveredQty);

        // Calculate arrived in current batch (status 20: UnderArbeid/Ankommet)
        // These are saved receipt lines not yet finished — NOT included in quantityDelivered
        const arrivedQuantity = receivingLine
            ? receivingLine.receivedTransactions
                .filter(t => t.lineStatus === StcTransLineStatus.UnderArbeid)
                .reduce((sum, t) => sum + (t.quantity || 0), 0)
            : 0;

        // When editing an existing transaction (status 20), subtract its current quantity to avoid double-counting.
        // The existing transaction's qty is already included in arrivedQuantity,
        // and newQuantity is the replacement value, not an addition.
        const editingAdjustment = editingTransactionQty || 0;

        console.log('  - arrivedQuantity (status 20):', arrivedQuantity);
        console.log('  - editingAdjustment (subtract for edit):', editingAdjustment);

        // Total = delivered (includes all status >= 50) + arrived (status 20) - editing old qty + new qty
        const totalAfterReceipt = deliveredQty + arrivedQuantity - editingAdjustment + newQuantity;
        const totalAlreadyReceived = deliveredQty + arrivedQuantity - editingAdjustment;
        console.log('  - totalAfterReceipt:', totalAfterReceipt);

        // Check if over-receiving
        if (totalAfterReceipt > orderedQty) {
            const overQty = totalAfterReceipt - orderedQty;
            console.log('  ⚠️  OVER-RECEIVING DETECTED!');
            console.log('  - overQty:', overQty);
            console.log('  - userProfile:', userProfile);
            console.log('  - extraQtyOnGRNotAllowed:', userProfile?.extraQtyOnGRNotAllowed);
            console.log('  - extraQtyOnGRConfirm:', userProfile?.extraQtyOnGRConfirm);

            // Check profile settings:
            // 1. ExtraQtyOnGRNotAllowed = true → HARD BLOCK
            // 2. ExtraQtyOnGRConfirm = true → WARN with confirmation modal
            // 3. Both false → Allow silently
            if (userProfile?.extraQtyOnGRNotAllowed) {
                console.log('  🚫 BLOCKING over-receive');
                // BLOCK completely
                return {
                    allowed: false,
                    message: t('warehouse.modules.receipt.validation.cannotReceiveMoreThanOrdered', {
                        orderedQty,
                        unit: orderLine.itemUnitTxt || t('warehouse.general.unitDefault', {}, 'stk'),
                        totalReceived: totalAlreadyReceived,
                        newQuantity,
                        overQty
                    }, `Kan ikke motta mer enn bestilt antall (${orderedQty} ${orderLine.itemUnitTxt || 'stk'}). Allerede mottatt: ${totalAlreadyReceived} ${orderLine.itemUnitTxt || 'stk'}. Forsøk på å motta: ${newQuantity} ${orderLine.itemUnitTxt || 'stk'} (${overQty} ${orderLine.itemUnitTxt || 'stk'} over).`),
                    isWarning: false
                };
            } else if (userProfile?.extraQtyOnGRConfirm) {
                console.log('  ⚠️  WARNING over-receive (requiring confirmation)');
                // WARN with confirmation modal
                return {
                    allowed: true,
                    message: t('warehouse.modules.receipt.validation.overReceiveWarning', {
                        orderedQty,
                        unit: orderLine.itemUnitTxt || t('warehouse.general.unitDefault', {}, 'stk'),
                        totalAfterReceipt,
                        overQty
                    }, `OBS: Du mottar mer enn bestilt antall. Bestilt: ${orderedQty} ${orderLine.itemUnitTxt || 'stk'}, Totalt mottak vil bli: ${totalAfterReceipt} ${orderLine.itemUnitTxt || 'stk'} (${overQty} ${orderLine.itemUnitTxt || 'stk'} over).`),
                    isWarning: true
                };
            } else {
                console.log('  ✅ Over-receive allowed silently (no profile restrictions)');
                // Both settings false → Allow silently without warning
                return { allowed: true, message: '', isWarning: false };
            }
        }

        console.log('  ✅ No over-receiving');
        // No over-receiving
        return { allowed: true, message: '', isWarning: false };
    };

    // Process global barcode scan - uses the centralized barcode processing helper
    const processGlobalBarcode = (scanInput: string) => {
        const result = processBarcodeInput(scanInput);
        if (result) {
            handleGS1Scan({
                itemNo: result.itemNo,
                quantity: result.quantity,
                batchNo: result.batchNo,
                expiryDate: result.expiryDate,
                sscc: result.sscc
            });
        }
    };

    const handleGS1Scan = (data: { itemNo?: string; quantity?: number; sscc?: string; batchNo?: string; expiryDate?: string }) => {
        console.log('GS1 Scan data received:', data);

        if (!data.itemNo) {
            console.warn('No itemNo in scan data');
            return;
        }

        // Find matching line in availableOrderLines
        const itemNoLower = data.itemNo.toLowerCase();
        const matchedLine = availableOrderLines.find(
            line => line.itemNo?.toLowerCase() === itemNoLower ||
                    line.itemBarCode?.toLowerCase() === itemNoLower
        );

        if (matchedLine) {
            console.log('Matched order line:', matchedLine);
            console.log('Barcode contains batch info:', { batchNo: data.batchNo, expiryDate: data.expiryDate });

            // Clear editing mode - this is a new scan
            setEditingStcTrans(null);
            setSelectedSearchLine(matchedLine);

            // Set quantity from GS1 data or calculate remaining quantity
            const remainingQty = calculateRemainingQuantity(matchedLine, matchedLine.lineNo);
            const quantity = data.quantity || remainingQty;
            setNewReceipt({
                lineNo: matchedLine.lineNo,
                quantity: quantity,
                orderNo: matchedLine.orderNo
            });

            // Store scanned batch info if present (will be picked up by ReceivingActiveLine)
            setScannedBatchInfo({
                batchNo: data.batchNo,
                expDate: data.expiryDate
            });

            // Auto-focus disabled - global scanning is active
            // setTimeout(() => {
            //     if (quantityInputRef.current) {
            //         quantityInputRef.current.focus();
            //         quantityInputRef.current.select();
            //     }
            // }, 100);
        } else {
            alert(t('warehouse.modules.receipt.messages.itemNotFoundInOrder', {}, 'Vare ikke funnet i ordren!'));
            console.warn('No matching line found for itemNo:', data.itemNo);
        }
    };

    const getStatusColor = (remainingQuantity: number, totalQuantity: number) => {
        if (remainingQuantity === 0) return 'bg-green-100 text-green-800';
        if (remainingQuantity < totalQuantity) return 'bg-yellow-100 text-yellow-800';
        return 'bg-gray-100 text-gray-800';
    };

    const getStatusText = (remainingQuantity: number, totalQuantity: number) => {
        if (remainingQuantity === 0) return t('warehouse.modules.receipt.status.complete', {}, 'Komplett');
        if (remainingQuantity < totalQuantity) return t('warehouse.modules.receipt.status.partiallyReceived', {}, 'Delvis mottatt');
        return t('warehouse.modules.receipt.status.notReceived', {}, 'Ikke mottatt');
    };

    // Add PO to bilmottak handlers
    const handleOpenAddPO = async () => {
        if (!bilmottakBatchId) return;
        try {
            setAddPOLoading(true);
            const orders = await pOrdersService.getOrdersForReceiving();
            // Filter out orders already linked to this batch
            const linkedOrderNos = new Set(receivingLines.map(l => l.orderLine.orderNo));
            const available = orders.filter(
                o => !linkedOrderNos.has(o.orderNo) && (!o.warehouse || o.warehouse === (bilmottakInfo?.warehouse || ''))
            );
            setAddPOAvailableOrders(available);
            setAddPOSelectedNos(new Set());
            setAddPOSearchTerm('');
            setShowAddPOModal(true);
        } catch (err) {
            console.error('Error loading orders for add PO:', err);
        } finally {
            setAddPOLoading(false);
        }
    };

    const handleConfirmAddPO = async () => {
        if (!bilmottakBatchId || addPOSelectedNos.size === 0) return;
        try {
            setAddPOLoading(true);
            const result = await bilmottakService.linkOrders(bilmottakBatchId, Array.from(addPOSelectedNos));
            if (result.success) {
                setShowAddPOModal(false);
                // Reload order lines to include newly linked POs
                await loadOrderData(true);
            } else {
                setError(result.errorMessage || 'Kunne ikke koble ordrer');
            }
        } catch (err) {
            console.error('Error linking orders:', err);
            setError('Feil ved kobling av ordrer');
        } finally {
            setAddPOLoading(false);
        }
    };

    const filteredAddPOOrders = addPOAvailableOrders.filter(o => {
        if (!addPOSearchTerm.trim()) return true;
        const term = addPOSearchTerm.toLowerCase();
        return o.orderNoERP?.toLowerCase().includes(term) ||
            o.supplierName?.toLowerCase().includes(term) ||
            String(o.orderNo).includes(term);
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex items-center space-x-2">
                    <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
                    <span className="text-lg text-gray-600">{t('warehouse.modules.receipt.messages.loadingOrderInfo', {}, 'Henter ordre-informasjon...')}</span>
                </div>
            </div>
        );
    }

    if (showSuccessMessage) {
        return (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <div>
                        <h3 className="text-green-800 font-medium">{t('warehouse.modules.receipt.messages.receivingComplete', {}, 'Mottak avsluttet!')}</h3>
                        <p className="text-green-700 text-sm mt-1">
                            {t('warehouse.modules.receipt.messages.returningToList', {}, 'Status er endret til "Klar for eksport". Returnerer til mottakslisten...')}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <div className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-yellow-500" />
                    <span className="text-yellow-700">{t('warehouse.modules.receipt.errors.orderNotFound', {}, 'Ordre ikke funnet')}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Mobile/Enterprise scanner support (Zebra, etc.) - invisible input that captures scanner data */}
            <MobileBarcodeCaptureInput
                enabled={barcodeScannerEnabled && !isAnyModalOpen}
                onScan={(barcode) => {
                    console.log('📱 Mobile scanner detected barcode:', barcode);
                    processGlobalBarcode(barcode);
                }}
            />

            {/* Bulk processing overlay */}
            {isBulkProcessing && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md">
                        <div className="flex items-center space-x-4">
                            <RefreshCw className="h-8 w-8 text-blue-500 animate-spin" />
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">{t('warehouse.modules.receipt.messages.processingReceipt', {}, 'Prosesserer mottak')}</h3>
                                <p className="text-sm text-gray-600 mt-1">{bulkProcessingMessage}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Header - Compact design with buttons on same line as back */}
            <div className="space-y-2">
                {/* Top row: Back button + Action buttons */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <BackButton onClick={onBack} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200" />
                        <MediaCaptureButton
                            entityType={receivingMode === 'bilmottak' ? 'Bilmottak' : 'POrder'}
                            entityKey={receivingMode === 'bilmottak' ? (bilmottakBatchId || '') : (orderNo?.toString() || '')}
                        />
                    </div>

                    {/* Action buttons on right */}
                    <div className="flex items-center gap-2">
                        {receivingMode === 'bilmottak' && bilmottakBatchId && (
                            <button
                                onClick={handleOpenAddPO}
                                disabled={addPOLoading}
                                className="flex items-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                                title="Legg til innkjøpsordrer"
                            >
                                <Plus className="h-4 w-4" />
                                <span className="hidden sm:inline">Legg til ordre</span>
                            </button>
                        )}
                        <button
                            onClick={() => setShowLineBrowser(true)}
                            className="flex items-center gap-1 px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
                            title={t('warehouse.modules.receipt.actions.viewAllLines', {}, 'Se alle ordrelinjer')}
                        >
                            <List className="h-4 w-4" />
                            <span className="hidden sm:inline">{t('warehouse.modules.receipt.labels.lines', {}, 'Linjer')}</span>
                        </button>
                        <button
                            onClick={() => loadOrderData()}
                            className="flex items-center gap-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                            title={t('common.buttons.refresh', {}, 'Oppdater')}
                        >
                            <RefreshCw className="h-4 w-4" />
                        </button>

                        {hasTransactions && (
                            <>
                                <button
                                    onClick={handleFinishReceiving}
                                    disabled={!canFinishReceiving || isFinishingReceiving}
                                    className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors text-sm ${
                                        canFinishReceiving && !isFinishingReceiving
                                            ? 'bg-green-500 text-white hover:bg-green-600'
                                            : 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                    }`}
                                    title={!canFinishReceiving ? t('warehouse.modules.receipt.messages.cannotFinishYet', {}, 'Mottaket kan ikke avsluttes ennå') : t('warehouse.modules.receipt.actions.finishReceiving', {}, 'Avslutt mottak')}
                                >
                                    {isFinishingReceiving ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Check className="h-4 w-4" />
                                    )}
                                    <span className="hidden sm:inline">{isFinishingReceiving ? t('warehouse.modules.receipt.actions.finishing', {}, 'Avslutter...') : t('warehouse.modules.receipt.actions.finish', {}, 'Avslutt')}</span>
                                </button>

                                <button
                                    onClick={handleFinishReceivingAndPutAway}
                                    disabled={!canFinishReceiving || isFinishingReceiving}
                                    className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors text-sm ${
                                        canFinishReceiving && !isFinishingReceiving
                                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                                            : 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                    }`}
                                    title={!canFinishReceiving ? t('warehouse.modules.receipt.messages.cannotFinishYet', {}, 'Mottaket kan ikke avsluttes ennå') : t('warehouse.modules.receipt.actions.finishAndPutAway', {}, 'Avslutt og gå til plassering')}
                                >
                                    {isFinishingReceiving ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <ArrowRight className="h-4 w-4" />
                                    )}
                                    <span className="hidden sm:inline">{isFinishingReceiving ? t('warehouse.modules.receipt.actions.processing', {}, 'Prosesserer...') : t('warehouse.modules.receipt.actions.receiptAndPutAway', {}, 'Mottak & Plassering')}</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Order info below buttons, aligned left */}
                <div className="ml-0">
                    {receivingMode === 'bilmottak' ? (
                        <>
                            <h1 className="text-lg font-bold text-purple-700 flex items-center gap-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-purple-100 text-purple-700 text-xs font-medium">
                                    Bilmottak
                                </span>
                                {bilmottakInfo?.name || bilmottakBatchId}
                            </h1>
                            <p className="text-sm text-gray-600">{bilmottakInfo?.warehouse || 'Flerbruker-mottak'}</p>
                        </>
                    ) : receivingMode === 'shipment' ? (
                        <>
                            <h1 className="text-lg font-bold text-teal-700 flex items-center gap-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-teal-100 text-teal-700 text-xs font-medium">
                                    Forsendelse
                                </span>
                                {shipmentInfo?.shipmentNo || inboundShipmentNo}
                            </h1>
                            <p className="text-sm text-gray-600">{shipmentInfo?.supplierNames || order.supplierName}</p>
                        </>
                    ) : (
                        <>
                            <h1 className="text-lg font-bold text-gray-900">
                                {t('warehouse.modules.receipt.orderHeader', { orderNo: order.orderNoERP }, `Ordre ${order.orderNoERP}`)}{order.supplierOrderNo && ` | ${order.supplierOrderNo}`}
                            </h1>
                            <p className="text-sm text-gray-600">{order.supplierName}</p>
                        </>
                    )}
                </div>
            </div>

            {/* GS1 Scanner - Enhanced with full GS1-128 and plain barcode support */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <div className="flex gap-2">
                    <input
                        ref={scannerRef}
                        type="text"
                        placeholder={t('warehouse.modules.receipt.placeholders.scanBarcode', {}, 'Skann strekkode (GS1-128 eller vanlig varenummer)')}
                        className="flex-1 p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                const target = e.target as HTMLInputElement;
                                const value = target.value.trim();
                                if (value) {
                                    processGlobalBarcode(value);
                                    target.value = '';
                                }
                            }
                        }}
                    />
                </div>
                {/*<div className="text-xs text-gray-500 mt-2">*/}
                {/*    Støtter både GS1-128 (med batch/utløpsdato) og vanlige strekkoder*/}
                {/*</div>*/}
            </div>

            {/* Error message - vises inline, ikke som early return */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <AlertCircle className="h-5 w-5 text-red-500" />
                            <span className="text-red-700">{error}</span>
                        </div>
                        <button
                            onClick={() => setError(null)}
                            className="text-red-500 hover:text-red-700"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Warning Message */}
            {warningMessage && (
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
                    <div className="flex items-center">
                        <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
                        <p className="text-yellow-700">{warningMessage}</p>
                    </div>
                </div>
            )}

            {/* Active Line Component */}
            {selectedSearchLine && newReceipt && (() => {
                // Calculate quantities from transactions for the selected line
                const receivingLine = findReceivingLine(newReceipt.lineNo, newReceipt.orderNo ?? selectedSearchLine?.orderNo);

                // Quantity delivered in previous completed batches
                const previouslyDelivered = selectedSearchLine.quantityDelivered || 0;

                // Only sum transactions with status = 20 (UnderArbeid) for arrivedQuantity
                const arrivedQuantity = receivingLine
                    ? receivingLine.receivedTransactions
                        .filter(t => t.lineStatus === StcTransLineStatus.UnderArbeid)
                        .reduce((sum, t) => sum + (t.quantity || 0), 0)
                    : 0;

                // quantityDelivered is authoritative for all completed deliveries (status >= 50)
                // Don't separately count currentBatch status >= 50 — already in quantityDelivered
                const totalReceived = previouslyDelivered;

                // Calculate remainingQuantity: Ordered - Delivered - Arrived
                const remainingQuantity = (selectedSearchLine.quantity || 0) - previouslyDelivered - arrivedQuantity;

                return (
                    <ReceivingActiveLine
                        line={{
                            itemNo: selectedSearchLine.itemNo,
                            description1: selectedSearchLine.description1 || t('common.noDescription', {}, 'Ingen beskrivelse'),
                            quantity: selectedSearchLine.quantity || 0,
                            totalReceived: totalReceived,
                            arrivedQuantity: arrivedQuantity,
                            remainingQuantity: remainingQuantity,
                            itemUnitTxt: selectedSearchLine.itemUnitTxt || t('warehouse.general.unitDefault', {}, 'stk'),
                            itemType: selectedLineItemType
                        }}
                        editingStcTrans={editingStcTrans ? {
                            itemBatchNo: editingStcTrans.itemBatchNo || '',
                            itemBatchExpDate: editingStcTrans.itemBatchExpDate ? new Date(editingStcTrans.itemBatchExpDate).toISOString().split('T')[0] : '',
                            quantity: editingStcTrans.quantity || 0,
                            itemType: editingStcTrans.itemType
                        } : null}
                        currentQuantity={editingStcTrans ? editingStcTrans.quantity || 0 : newReceipt.quantity}
                        scannedBatchInfo={scannedBatchInfo}
                        validationError={validationError}
                        validationWarning={validationWarning}
                        onQuantityChange={(qty) => {
                            // Clear validation when quantity changes
                            setValidationError('');
                            setValidationWarning('');

                            if (editingStcTrans) {
                                // When editing, update the editingStcTrans object directly
                                setEditingStcTrans({ ...editingStcTrans, quantity: qty });
                            } else {
                                // When creating new, update newReceipt
                                setNewReceipt({ ...newReceipt, quantity: qty });
                            }
                        }}
                        onBatchInfoChange={(batchNo, expDate) => {
                            if (editingStcTrans) {
                                setEditingStcTrans({
                                    ...editingStcTrans,
                                    itemBatchNo: batchNo,
                                    itemBatchExpDate: expDate ? new Date(expDate) : undefined
                                });
                            }
                            // For new receipts, batch info is handled internally in ReceivingActiveLine
                        }}
                        onSave={(qty, batchNo, expDate, receivingLocation) => {
                            handleAddReceipt(newReceipt.lineNo, qty, batchNo, expDate, receivingLocation);
                            // Clear scanned batch info after save
                            setScannedBatchInfo({});
                        }}
                        onCancel={() => {
                            setNewReceipt(null);
                            setSelectedSearchLine(null);
                            setEditingStcTrans(null);
                            setScannedBatchInfo({});
                            setValidationError('');
                            setValidationWarning('');
                        }}
                        onDirectReceipt={editingStcTrans
                            ? (qty, batchNo, expDate, receivingLocation) => {
                                // For editing existing: validate over-receiving before opening direktemottak modal
                                const rl = findReceivingLine(editingStcTrans.lineNo!, selectedSearchLine?.orderNo);
                                if (rl?.orderLine) {
                                    const origQty = rl.receivedTransactions.find(t => t.lineID === editingStcTrans.lineID)?.quantity || 0;
                                    const validation = validateOverReceiving(rl.orderLine, qty, rl, origQty);
                                    if (!validation.allowed) {
                                        setValidationError(validation.message);
                                        setValidationWarning('');
                                        return;
                                    } else if (validation.isWarning) {
                                        setOverReceiveModalMessage(validation.message);
                                        setPendingOverReceiveAction(() => async () => {
                                            handleDirectReceipt(editingStcTrans);
                                        });
                                        setShowOverReceiveConfirmModal(true);
                                        return;
                                    }
                                }
                                handleDirectReceipt(editingStcTrans);
                            }
                            : (qty, batchNo, expDate, receivingLocation) => {
                                // For new receipts: create StcTrans first, then open direktemottak modal
                                handleDirectReceiptFromNew(newReceipt.lineNo, qty, batchNo, expDate, receivingLocation);
                                // Clear scanned batch info after direct receipt
                                setScannedBatchInfo({});
                            }
                        }
                        onDelete={editingStcTrans ? () => handleDeleteReceipt(editingStcTrans.lineID, editingStcTrans.id!) : undefined}
                        showImage={false}
                        onOpenGrunndata={allowAdvItemConfig ? () => handleOpenAdvancedConfig(selectedSearchLine.itemNo) : undefined}
                        onPrintLabel={(reportId, numLabels) => handlePrintLabel(reportId, numLabels)}
                        showPrintSection={true}
                        companyNo={order.companyNo}
                        warehouseID={order.warehouse || 'MAIN'}
                        defaultReceivingLocation={userProfile?.defaultGRBinCode}
                    />
                );
            })()}

            {/* Combined Search Box replaced with GS1Scanner + ActiveLine above */}

            {/* Table layout with sticky header */}
            {(receivingLines.some(line => line.receivedTransactions.length > 0) || newReceipt) && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            {/* Sticky header */}
                            <thead className="bg-blue-100 sticky top-0 z-10">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.general.itemNumber', {}, 'Varenr')}</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.ordered', {}, 'Bestilt')}</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.arrived', {}, 'Ankommet')}</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.arrivalLocation', {}, 'Ankomstlokasjon')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Active (unlocked) lines */}
                                {(() => {
                                    // Flatten all active transactions first to get proper sequential index
                                    const activeRows = receivingLines.flatMap((receivingLine) => {
                                        if (receivingLine.receivedTransactions.length > 0) {
                                            return receivingLine.receivedTransactions
                                                .filter(trans => trans.lineStatus < 50)
                                                .map((trans) => ({ receivingLine, trans }));
                                        }
                                        return [];
                                    });

                                    return activeRows.map((row, index) => {
                                        const { receivingLine, trans } = row;

                                        // Initialize batch edit state
                                        initializeBatchEdit(trans);
                                        const transId = `${trans.id}`;
                                        const cachedEditState = batchEditState[transId];

                                        // Get arrived quantity (transaction quantity)
                                        const arrivedQty = cachedEditState?.isEditingQuantity ? cachedEditState.quantity : (trans.quantity || 0);

                                        // Get ordered quantity
                                        const orderedQty = receivingLine.orderLine.quantity || 0;

                                        // Alternating row colors - index 0 = white, index 1 = gray, etc.
                                        const bgColor = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';

                                        return (
                                            <tr
                                                key={`${receivingLine.orderLine.orderNo}-${receivingLine.orderLine.lineNo}-${trans.id}`}
                                                className={`${bgColor} hover:bg-blue-50 cursor-pointer transition-colors`}
                                                onClick={() => handleStcTransClick(trans, receivingLine.orderLine)}
                                            >
                                                <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                                                    {receivingLine.orderLine.itemNo}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700">
                                                    <div>{receivingLine.orderLine.description1 || t('common.noDescription', {}, 'Ingen beskrivelse')}</div>
                                                    {trans.itemSerialNo && (
                                                        <div className="text-xs font-mono text-purple-600 mt-0.5">SN: {trans.itemSerialNo}</div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 whitespace-nowrap">
                                                    {orderedQty} <span className="font-normal text-gray-500">{receivingLine.orderLine.itemUnitTxt}</span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-right font-medium text-blue-600 whitespace-nowrap">
                                                    {arrivedQty} <span className="font-normal text-gray-500">{receivingLine.orderLine.itemUnitTxt}</span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                                                    {trans.locationName || '-'}
                                                </td>
                                            </tr>
                                        );
                                    });
                                })()}
                            </tbody>
                        </table>
                    </div>

                    {/* Locked lines panel (collapsed by default) */}
                    {receivingLines.some(line => line.receivedTransactions.some(trans => trans.lineStatus >= 50)) && (
                        <div className="border-t border-gray-200">
                            <button
                                onClick={() => setShowLockedLines(!showLockedLines)}
                                className="flex items-center justify-between w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 transition-colors"
                            >
                                <div className="flex items-center space-x-2">
                                    <Package className="h-5 w-5 text-gray-600" />
                                    <span className="font-medium text-gray-700">
                                        {t('warehouse.modules.receipt.labels.completedReceipts', { count: receivingLines.flatMap(line => line.receivedTransactions.filter(trans => trans.lineStatus >= 50)).length }, `Fullførte mottak (${receivingLines.flatMap(line => line.receivedTransactions.filter(trans => trans.lineStatus >= 50)).length})`)}
                                    </span>
                                </div>
                                {showLockedLines ? (
                                    <ChevronDown className="h-5 w-5 text-gray-600" />
                                ) : (
                                    <ChevronRight className="h-5 w-5 text-gray-600" />
                                )}
                            </button>

                            {showLockedLines && (() => {
                                // Group locked transactions by orderNo, lineNo, itemNo
                                const groupedLines = receivingLines
                                    .filter(rl => rl.receivedTransactions.some(t => t.lineStatus >= 50))
                                    .map(receivingLine => ({
                                        key: `${receivingLine.orderLine.orderNo}-${receivingLine.orderLine.lineNo}-${receivingLine.orderLine.itemNo}`,
                                        orderLine: receivingLine.orderLine,
                                        // Count of locked transactions for this line
                                        transactionCount: receivingLine.receivedTransactions.filter(t => t.lineStatus >= 50).length
                                    }));

                                // Remove duplicates (same order line might appear multiple times)
                                const uniqueLines = groupedLines.filter((line, index, self) =>
                                    index === self.findIndex(l => l.key === line.key)
                                );

                                return (
                                    <table className="min-w-full">
                                        {/* Header for locked lines */}
                                        <thead className="bg-blue-100">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.general.itemNumber', {}, 'Varenr')}</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                                                <th className="px-4 py-3 text-right text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.ordered', {}, 'Bestilt')}</th>
                                                <th className="px-4 py-3 text-right text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.received', {}, 'Mottatt')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {uniqueLines.map((line, index) => {
                                                // Alternating row colors
                                                const bgColor = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';

                                                // Get quantities
                                                const orderedQty = line.orderLine.quantity || 0;
                                                const deliveredQty = line.orderLine.quantityDelivered || 0;

                                                return (
                                                    <tr key={line.key} className={bgColor}>
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-700 whitespace-nowrap">
                                                            {line.orderLine.itemNo}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">
                                                            {line.orderLine.description1 || t('common.noDescription', {}, 'Ingen beskrivelse')}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-700 whitespace-nowrap">
                                                            {orderedQty} <span className="font-normal text-gray-500">{line.orderLine.itemUnitTxt}</span>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-right font-medium text-green-600 whitespace-nowrap">
                                                            {deliveredQty} <span className="font-normal text-gray-500">{line.orderLine.itemUnitTxt}</span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                );
                            })()}
                        </div>
                    )}
                </div>
            )}

            {/* Melding når ingen linjer har StcTrans */}
            {!receivingLines.some(line => line.receivedTransactions.length > 0) && !newReceipt && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="flex items-center space-x-2">
                        <Package className="h-5 w-5 text-blue-500" />
                        <div>
                            <h3 className="text-blue-800 font-medium">{t('warehouse.modules.receipt.messages.noReceivedItemsYet', {}, 'Ingen mottatte varer ennå')}</h3>
                            <p className="text-blue-700 text-sm mt-1">
                                {t('warehouse.modules.receipt.messages.startReceivingInstructions', {}, 'Bruk søkefeltet ovenfor eller "Alle ordrelinjer" for å starte mottaket. Mottatte varer vil vises her med detaljerte StcTrans oppføringer.')}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Line browser modal */}
            {showLineBrowser && (
                <POrderLineBrowser
                    orderNo={orderNo !== undefined ? orderNo : (availableOrderLines[0]?.orderNo ?? 0)}
                    orderLines={availableOrderLines}
                    batchId={receivingMode === 'bilmottak' || receivingMode === 'shipment' ? batchId : undefined}
                    onLineSelect={(line) => {
                        // Clear editing mode - this is selecting a new line
                        setEditingStcTrans(null);
                        setSelectedSearchLine(line);
                        const remainingQty = calculateRemainingQuantity(line, line.lineNo);
                        setNewReceipt({ lineNo: line.lineNo, quantity: remainingQty, orderNo: line.orderNo });
                        setShowLineBrowser(false);
                    }}
                    onAllLinesSelect={async (lines) => {
                        // Bulk-registrering av alle valgte linjer med bestilt antall
                        setShowLineBrowser(false);

                        // Sett prosesseringstilstand
                        setIsBulkProcessing(true);
                        setBulkProcessingMessage(t('warehouse.modules.receipt.messages.registeringLines', { count: lines.length }, `Registrerer ${lines.length} linjer automatisk...`));
                        setError(null);

                        // Kjør bulk-registrering i bakgrunnen
                        setTimeout(async () => {
                            try {
                                // Hent profil én gang for alle linjer
                                let locationName = '';
                                try {
                                    const profile = await userProfileService.getCurrentUserProfile();
                                    locationName = profile.defaultGRBinCode || '';
                                } catch (profileError) {
                                    console.warn('Kunne ikke hente profil for LocationName:', profileError);
                                }

                                // Ensure TransOrder exists once
                                if (order) {
                                    await stcTransService.ensureTransOrderExists(batchId, order);
                                }

                                // Forbered alle transaksjoner
                                const transactions: CreateStcTransRequest[] = lines
                                    .filter(line => (line.quantity || 0) > 0)
                                    .map((line) => {
                                        const quantityToReceive = line.quantity || 0;
                                        const erpLinkName = order.orderNoERP + '-' + line.lineNoERP;

                                        return {
                                            batchID: batchId,
                                            lineType: 0,
                                            orderNo: line.orderNo, // Use line's orderNo for shipment mode support
                                            lineNo: line.lineNo,
                                            locationName: locationName,
                                            quantity: quantityToReceive,
                                            transDt: new Date(),
                                            warehouse: order.warehouse || 'MAIN',
                                            toWarehouse: order.warehouse,
                                            // operatorID utelates - API setter innlogget operatør fra JWT
                                            itemNo: line.itemNo,
                                            itemBarCode: line.itemBarCode,
                                            description1: line.description1,
                                            itemUnitCode: line.itemUnitCode,
                                            itemUnitTxt: line.itemUnitTxt,
                                            transType: 0,
                                            orderNoERP: order.orderNoERP,
                                            lineNoERP: line.lineNoERP,
                                            erpLinkName: erpLinkName,
                                            // itemType hentes separat per vare og er ikke tilgjengelig på POrderLineDto
                                            itemBatchNo: '',
                                            itemBatchExpDate: undefined,
                                            lineStatus: StcTransLineStatus.UnderArbeid
                                        };
                                    });

                                // Kall bulk-endpoint
                                await stcTransService.createTransactionsBulk(batchId, transactions);

                                // Vis suksessmelding
                                setBulkProcessingMessage(t('warehouse.modules.receipt.messages.linesRegistered', { count: lines.length }, `${lines.length} linjer registrert!`));

                                // Oppdater data og lukk prosesseringsindikator
                                await loadOrderData();

                                // Re-check if receiving can be finished after bulk registration
                                if (batchId) {
                                    await checkCanFinishReceiving(batchId);
                                }

                                setTimeout(() => {
                                    setIsBulkProcessing(false);
                                }, 500);

                            } catch (error) {
                                console.error('Feil ved bulk-registrering:', error);
                                setError(t('warehouse.modules.receipt.errors.bulkRegistrationFailed', {}, 'Feil ved registrering av linjer. Se konsollen for detaljer.'));
                                setIsBulkProcessing(false);
                            }
                        }, 100);
                    }}
                    onClose={() => setShowLineBrowser(false)}
                />
            )}

            {/* Put-Away Modal */}
            {showPutAwayModal && selectedStcTrans && order && (
                <PutAwayModal
                    isOpen={showPutAwayModal}
                    onClose={handleModalClose}
                    stcTrans={selectedStcTrans}
                    companyNo={order.companyNo}
                    warehouseID={order.warehouse || 'MAIN'}
                    onSuccess={handleModalSuccess}
                />
            )}

            {/* Edit StcTrans Modal */}
            {showEditModal && selectedStcTrans && (
                <StcTransEditModal
                    isOpen={showEditModal}
                    onClose={handleModalClose}
                    stcTrans={selectedStcTrans}
                    onSuccess={handleModalSuccess}
                />
            )}

            {/* Direct Receipt Modal */}
            {showDirectReceiptModal && selectedStcTrans && order && (
                <DirectReceiptModal
                    isOpen={showDirectReceiptModal}
                    onClose={handleModalClose}
                    stcTrans={selectedStcTrans}
                    companyNo={order.companyNo}
                    warehouseID={order.warehouse || 'MAIN'}
                    onSuccess={handleModalSuccess}
                />
            )}

            {/* Advanced Item Config Modal */}
            {showAdvancedConfigModal && advancedConfigItemNo && order && (
                <AdvancedItemConfigModal
                    isOpen={showAdvancedConfigModal}
                    onClose={handleCloseAdvancedConfig}
                    companyNo={order.companyNo}
                    itemNo={advancedConfigItemNo}
                    onSave={handleAdvancedConfigSaved}
                />
            )}

            {/* Over-Receive Confirmation Modal */}
            <OverReceiveConfirmModal
                isOpen={showOverReceiveConfirmModal}
                message={overReceiveModalMessage}
                onConfirm={handleConfirmOverReceive}
                onCancel={handleCancelOverReceive}
            />

            {/* Serial Number Receipt Modal (ItemType=6) */}
            {showSerialNumberModal && serialNumberPendingData && (
                <SerialNumberReceiptModal
                    isOpen={showSerialNumberModal}
                    onClose={() => {
                        setShowSerialNumberModal(false);
                        setSerialNumberPendingData(null);
                    }}
                    onComplete={handleSerialNumberComplete}
                    itemNo={serialNumberPendingData.itemNo}
                    itemDescription={serialNumberPendingData.itemDescription}
                    expectedQuantity={serialNumberPendingData.quantity}
                />
            )}

            {/* Add PO Modal (Bilmottak only) */}
            {showAddPOModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
                        <div className="bg-purple-600 text-white px-4 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Plus className="h-5 w-5" />
                                <span className="font-semibold">Legg til innkjøpsordrer</span>
                            </div>
                            <button onClick={() => setShowAddPOModal(false)} className="text-white hover:bg-purple-500 rounded-full p-1">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="p-4 space-y-3">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={addPOSearchTerm}
                                    onChange={(e) => setAddPOSearchTerm(e.target.value)}
                                    placeholder="Søk ordrenr, leverandør..."
                                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                                <Package className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            </div>
                            <div className="border border-gray-200 rounded-lg max-h-56 overflow-y-auto">
                                {filteredAddPOOrders.length === 0 ? (
                                    <div className="text-center py-6 text-gray-400 text-sm">
                                        {addPOAvailableOrders.length === 0 ? 'Ingen tilgjengelige ordrer' : 'Ingen treff'}
                                    </div>
                                ) : (
                                    filteredAddPOOrders.map((o) => {
                                        const sel = addPOSelectedNos.has(o.orderNo);
                                        return (
                                            <button
                                                key={o.orderNo}
                                                type="button"
                                                onClick={() => {
                                                    setAddPOSelectedNos(prev => {
                                                        const n = new Set(prev);
                                                        sel ? n.delete(o.orderNo) : n.add(o.orderNo);
                                                        return n;
                                                    });
                                                }}
                                                className={`w-full px-3 py-2 flex items-center gap-3 text-left border-b border-gray-100 last:border-b-0 transition-colors ${sel ? 'bg-purple-50' : 'hover:bg-gray-50'}`}
                                            >
                                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${sel ? 'bg-purple-600 border-purple-600' : 'border-gray-300'}`}>
                                                    {sel && <Check className="h-3 w-3 text-white" />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <span className="font-medium text-sm text-gray-900">{o.orderNoERP || `#${o.orderNo}`}</span>
                                                    <span className="block text-xs text-gray-500 truncate">{o.supplierName || o.supplierNo}</span>
                                                </div>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                        <div className="bg-gray-50 px-4 py-3 flex justify-between items-center">
                            <span className="text-xs text-gray-500">{addPOSelectedNos.size} valgt</span>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setShowAddPOModal(false)}
                                    className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                                >
                                    Avbryt
                                </button>
                                <button
                                    onClick={handleConfirmAddPO}
                                    disabled={addPOSelectedNos.size === 0 || addPOLoading}
                                    className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                >
                                    {addPOLoading ? (
                                        <RefreshCw className="h-3 w-3 animate-spin" />
                                    ) : (
                                        <Plus className="h-3 w-3" />
                                    )}
                                    Koble ordrer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};